import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Send, MessageSquare, Megaphone, Paperclip, X, Loader2, FileText, Image as ImageIcon, FileSpreadsheet } from "lucide-react";
import { copilotChat, copilotUploadFiles } from "@/lib/store";
import CopilotPromos from "@/pages/admin/CopilotPromos";

const EXAMPLES = [
  "Aggiorna il listino New Wave con questo file",
  "Confronta questo listino con quello precedente",
  "Estrai le offerte da queste locandine",
  "Mostrami solo le variazioni superiori al 20%",
  "Mostrami gli analytics",
];

const kindIcon = (k) => (k === "image" ? ImageIcon : k === "sheet" ? FileSpreadsheet : FileText);

export default function Copilot() {
  const [view, setView] = useState("chat");
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Ciao 👋 Sono il Copilot amministrativo. Allega uno o più file (PDF, Excel, CSV o immagini) e scrivi cosa vuoi fare: aggiornare un listino fornitore, confrontare versioni, estrarre offerte dalle locandine, gestire prezzi/promo/margini. Cosa facciamo?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [attached, setAttached] = useState([]);
  const [uploading, setUploading] = useState(false);
  const sid = useRef(crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, busy]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      const data = await copilotUploadFiles(sid.current, files);
      const results = data.files || [];
      setAttached((a) => [...a, ...results]);
      const ok = results.filter((r) => r.status === "COMPLETATO").length;
      if (ok) toast.success(`${ok}/${results.length} file allegati`);
      const bad = results.filter((r) => r.status === "ERRORE");
      if (bad.length) toast.error(`${bad.length} file non allegati`, { description: bad.map((b) => `${b.filename}: ${b.error}`).join(" · ") });
    } catch {
      toast.error("Upload non riuscito");
    } finally { setUploading(false); }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    const attLabel = attached.filter((a) => a.status === "COMPLETATO").map((a) => a.filename);
    setMsgs((m) => [...m, { role: "user", text: content, files: attLabel }]);
    setBusy(true);
    try {
      const data = await copilotChat(sid.current, content);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, actions: data.actions || [] }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Errore nel Copilot. Riprova." }]);
    } finally { setBusy(false); }
  };

  const okAttached = attached.filter((a) => a.status === "COMPLETATO");

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="copilot-view-chat" onClick={() => setView("chat")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm ${view === "chat" ? "bg-[#2563EB] text-white" : "border border-white/10 text-zinc-300"}`}><MessageSquare className="h-4 w-4" /> Chat</button>
        <button data-testid="copilot-view-promos" onClick={() => setView("promos")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm ${view === "promos" ? "bg-[#2563EB] text-white" : "border border-white/10 text-zinc-300"}`}><Megaphone className="h-4 w-4" /> Promozioni</button>
      </div>
      {view === "promos" ? <CopilotPromos /> : (
    <div className="flex h-[70vh] flex-col rounded-xl border border-white/10 bg-[#11141A]" data-testid="copilot-panel">
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2563EB]"><Bot className="h-4 w-4 text-white" /></span>
        <div><p className="text-sm font-bold text-white">Copilot Admin</p><p className="text-[11px] text-zinc-500">Allega listini/locandine e opera in linguaggio naturale</p></div>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div data-testid={`copilot-msg-${m.role}`} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-[#2563EB] text-white" : "bg-white/5 text-zinc-200"}`}>
              {m.text}
              {(m.files || []).length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{m.files.map((fn, j) => <span key={j} className="rounded bg-black/20 px-1.5 py-0.5 text-[10px] text-white/80">📎 {fn}</span>)}</div>}
              {(m.actions || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1">{m.actions.map((a, j) => <span key={j} className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-400">✓ {a.tool}</span>)}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex gap-1 px-2"><span className="h-2 w-2 animate-bounce rounded-full bg-zinc-500" /><span className="h-2 w-2 animate-bounce rounded-full bg-zinc-500" style={{ animationDelay: "150ms" }} /><span className="h-2 w-2 animate-bounce rounded-full bg-zinc-500" style={{ animationDelay: "300ms" }} /></div>}
        {msgs.length === 1 && !busy && (
          <div className="space-y-2 pt-2">{EXAMPLES.map((s) => <button key={s} data-testid="copilot-example" onClick={() => send(s)} className="block w-full rounded-lg border border-white/10 px-3 py-2 text-left text-xs text-zinc-300 hover:border-[#2563EB]">{s}</button>)}</div>
        )}
      </div>
      {okAttached.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-white/10 px-3 pt-2" data-testid="copilot-attachments">
          {okAttached.map((a) => {
            const Icon = kindIcon(a.kind);
            return (
              <span key={a.id} data-testid="copilot-attachment-chip" className="flex items-center gap-1.5 rounded-full bg-[#0B0E13] border border-white/10 px-2.5 py-1 text-[11px] text-zinc-200">
                <Icon className="h-3.5 w-3.5 text-[#2563EB]" /> <span className="max-w-[160px] truncate">{a.filename}</span>
                <button data-testid="copilot-attachment-remove" onClick={() => setAttached((x) => x.filter((y) => y.id !== a.id))} className="text-zinc-500 hover:text-white"><X className="h-3 w-3" /></button>
              </span>);
          })}
        </div>
      )}
      <div className="border-t border-white/10 p-3">
        <div className="flex items-end gap-2">
          <input ref={fileRef} data-testid="copilot-file-input" type="file" multiple accept=".pdf,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp" onChange={onPick} className="hidden" />
          <button data-testid="copilot-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-zinc-300 hover:border-[#2563EB] disabled:opacity-40" title="Allega file">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </button>
          <textarea data-testid="copilot-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Es. Aggiorna il listino New Wave con questo file…" className="max-h-24 flex-1 resize-none rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#2563EB] focus:outline-none" />
          <button data-testid="copilot-send" onClick={() => send()} disabled={busy || !input.trim()} className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2563EB] text-white disabled:opacity-40"><Send className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
      )}
    </div>
  );
}
